import { useQuery } from '@tanstack/react-query';
import { backendClient } from '@/lib/backendClient';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { handleSecureError } from '@/utils/secureErrorHandler';
import { UserPreference, SessionFeedback } from '@/types/userPreferences';
import { userPreferencesKeys } from '@/hooks/queries/userPreferencesKeys';
import { useUserPreferencesMutations } from '@/hooks/mutations/useUserPreferencesMutations';

// Re-export types and keys for backward compatibility
export type { UserPreference, SessionFeedback } from '@/types/userPreferences';
export { userPreferencesKeys } from '@/hooks/queries/userPreferencesKeys';

export const useOptimizedUserPreferences = () => {
  const { user } = useAuth();
  const { toast } = useToast();

  // Fetch preferences with React Query
  const {
    data: preferences = [],
    isLoading,
    error,
    refetch
  } = useQuery({
    queryKey: userPreferencesKeys.list(user?.id || ''),
    queryFn: async () => {
      if (!user) return [];
      
      const { data, error } = await backendClient
        .from('user_preferences')
        .select('*')
        .eq('user_id', user.id)
        .order('confidence_score', { ascending: false });

      if (error) throw error;
      return data as UserPreference[];
    },
    enabled: !!user,
    staleTime: 10 * 60 * 1000, // 10 minutes - preferences change less frequently
    gcTime: 30 * 60 * 1000, // 30 minutes
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });

  // Get mutation functions from the dedicated mutations hook
  const {
    updatePreference,
    updatingPreference,
    submitSessionFeedback,
    submittingFeedback,
  } = useUserPreferencesMutations(preferences);

  const getRecommendedSoundscape = (category: string): string => {
    const soundscapePrefs = preferences.filter(
      p => p.preference_type === 'soundscape'
    );

    if (soundscapePrefs.length === 0) {
      const defaults = {
        'sleep_stories': 'rain',
        'meditation': 'forest',
        'focus_enhancement': 'white_noise',
        'stress_relief': 'ocean',
        'pain_management': 'gentle_stream',
        'explicit_meditation': 'deep_resonance'
      };
      return defaults[category as keyof typeof defaults] || 'nature_mix';
    }

    return soundscapePrefs.sort((a, b) => b.confidence_score - a.confidence_score)[0].preference_value;
  };

  const getBackgroundMusicSelection = (): string => {
    const musicPrefs = preferences.filter(
      p => p.preference_type === 'background_music_selection'
    );

    if (musicPrefs.length === 0) {
      return 'universal_background.mp3';
    }

    return musicPrefs.sort((a, b) => b.confidence_score - a.confidence_score)[0].preference_value;
  };

  // Handle errors
  if (error) {
    const secureError = handleSecureError(error, 'user_preferences_load');
    toast({
      title: "Error",
      description: secureError.userMessage,
      variant: "destructive",
    });
  }

  return {
    preferences,
    loading: isLoading,
    updatePreference,
    updatingPreference,
    submitSessionFeedback,
    submittingFeedback,
    getRecommendedSoundscape,
    getBackgroundMusicSelection,
    refreshPreferences: refetch,
    error
  };
};
